import {
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Stethoscope } from "lucide-react";

import type { Doctor } from "@/api/doctors";
import { Skeleton } from "@/ui";
import { tw } from "@/utils";

interface DoctorComboboxProps {
  value: Doctor | null;
  onChange: (value: Doctor | null) => void;
  options: Doctor[];
  isLoading: boolean;
  hasError: boolean;
  placeholder: string;
  onSearchChange: (search: string) => void;
}

export const DoctorCombobox = ({
  value,
  onChange,
  options,
  isLoading,
  placeholder,
  onSearchChange,
  hasError,
}: DoctorComboboxProps) => {
  const removeOption = () => {
    onChange(null);
  };

  return (
    <div className="flex flex-col gap-5">
      <HeadlessCombobox value={value} onChange={onChange}>
        <div className="relative">
          <ComboboxInput
            placeholder={placeholder}
            className={tw(
              "h-12 w-full rounded-lg border border-neutral-200 px-4 py-3 shadow-sm",
              "hover:border-primary-200",
              "focus:border-primary-300 focus:shadow-focus focus:outline-none",
              hasError && "border-error-500",
            )}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {value?.firstName && (
          <div className="relative flex w-full justify-between rounded-lg bg-primary-50 p-4">
            <div className="flex items-center">
              <Stethoscope className="text-primary-600" size={16} />
              <p className="pl-2 font-medium text-primary-900">
                {`${value.firstName} ${value.lastName}`}
              </p>
              <p className="pl-3 font-medium text-neutral-500">{`NPI ${value.npi}`}</p>
            </div>
            <div className="rounded-full bg-primary-100 p-2">
              <p className="text-primary-600">{value.specialty}</p>
            </div>
            <button
              type="button"
              onClick={removeOption}
              className="absolute right-1 top-1 text-primary-600 hover:text-primary-900"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        <ComboboxOptions
          anchor="bottom"
          transition
          className={tw(
            "flex w-[var(--input-width)] flex-col gap-2 rounded-md bg-white p-2 shadow-md empty:invisible",
            "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
          )}
        >
          {isLoading && (
            <div className="flex flex-col gap-2 py-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          )}
          {!isLoading && options.length === 0 && (
            <div className="py-2 text-center text-sm text-neutral-500">
              No doctors found.
            </div>
          )}
          {!isLoading && options.length > 0 && (
            <>
              <div className="grid grid-cols-4 gap-x-4 rounded-t-lg border border-neutral-100 bg-neutral-50 p-3 text-sm font-bold text-neutral-700">
                <p>Name</p>
                <p>NPI</p>
                <p>Practice Address</p>
                <p>Specialty</p>
              </div>
              {options.map((option: Doctor, index) => {
                const isSelected = value?.npi === option.npi;

                return (
                  <ComboboxOption
                    key={option.npi}
                    value={option}
                    className={({ focus }) =>
                      tw(
                        "grid cursor-default select-none grid-cols-4 gap-x-4 rounded-md text-sm uppercase text-primary-950",
                        focus ? "bg-primary-50" : "bg-white",
                        isSelected
                          ? "data-[selected]:border-primary-500 data-[selected]:bg-primary-100"
                          : "",
                        index !== 0 && "py-2",
                      )
                    }
                  >
                    <p className="font-bold">{`${option.lastName}, ${option.firstName}`}</p>
                    <p className="font-medium">{option.npi}</p>
                    <p>{option.practiceAddress}</p>
                    <p>{option.specialty}</p>
                  </ComboboxOption>
                );
              })}
            </>
          )}
        </ComboboxOptions>
      </HeadlessCombobox>
    </div>
  );
};
